import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Modal,
  Table,
  message,
  Checkbox,
  Select,
  Switch,
} from "antd";
import {
  UseCreateSubCaseSetting,
  UseUpdateSubCaseSetting,
  UseToggleCaseEnable,
} from "../hooks/useFetch";
import { useFetchCustomfieldCase } from "../../Customfield/Case/hooks/useFetch";
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';

function CaseFormTable({ form, caseId, cases, refetch }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [caseForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [selectedRecord, setSelectedRecord] = useState(null);
  const { mutate: createSubCase } = UseCreateSubCaseSetting();
  const { mutate: updateSubCase } = UseUpdateSubCaseSetting();
  const { mutateAsync: toggleEnable } = UseToggleCaseEnable();
  const [togglingId, setTogglingId] = useState(null);
  const companyId = sessionStorage.getItem("activeCompanyId");
  const { data: customFields = [] } = useFetchCustomfieldCase(companyId);

  // State สำหรับ customfield_subcase ใน Edit Modal
  const [editCustomFields, setEditCustomFields] = useState([]);

  // เมื่อเปิด modal edit ให้ sync customfield_subcase -> editCustomFields
  useEffect(() => {
    if (isEditModalOpen && selectedRecord) {
      let arr = selectedRecord.customfield_subcase
        ? selectedRecord.customfield_subcase.map((item, idx) => ({
            key: idx,
            value: item.value,
            title: item.customfield_name,
            number: item.number || idx + 1,
          }))
        : [];
      // ถ้าไม่มีเลย ให้เพิ่มแถวเปล่า 1 แถว (default เป็น customFields[0])
      if (arr.length === 0 && customFields.length > 0) {
        arr = [{
          key: Date.now(),
          value: customFields[0].id,
          title: customFields[0].title,
          number: 1,
        }];
      }
      setEditCustomFields(arr);
    }
    else {
      setEditCustomFields([]);
      console.log("editCustomFields", editCustomFields);
    }
  }, [isEditModalOpen, selectedRecord]);

  // เพิ่ม custom field row
  const handleAddCustomField = () => {
    const newNumber = editCustomFields.length + 1;
    setEditCustomFields([
      ...editCustomFields,
      {
        key: Date.now(),
        value: undefined, // dropdown ว่าง
        title: '',
        number: newNumber,
      }
    ]);
  };

  // ลบ custom field row
  const handleRemoveCustomField = (key) => {
    const newFields = editCustomFields.filter(f => f.key !== key);
    // อัพเดท number หลังจากลบ
    const updatedFields = newFields.map((field, idx) => ({ ...field, number: idx + 1 }));
    setEditCustomFields(updatedFields);
  };

  // เปลี่ยน custom field ใน row
  const handleChangeCustomField = (key, newId) => {
    const newField = customFields.find(f => f.id === newId);
    setEditCustomFields(editCustomFields.map(f =>
      f.key === key ? { ...f, value: newId, title: newField.title } : f
    ));
  };

  const data = (cases.find((item) => item.id === caseId)?.subcases || []).sort(
    (a, b) => a.number - b.number
  );

  const handleModalOk = async () => {
    try {
      const values = await caseForm.validateFields();
      const customFieldValues = editCustomFields.map((f) => {
        const field = customFields.find(cf => cf.id === f.value);
        return {
          type: field?.type || 1,
          value: field?.id || 0,
          customfield_name: field?.title || '',
          number: f.number,
        };
      });
      const newCase = {
        ...values,
        caseId: caseId,
        companyId: companyId,
        customfield_subcase: customFieldValues,
      };
      createSubCase(newCase);
      message.success("Case added successfully");
      setIsModalOpen(false);
      caseForm.resetFields();
      setEditCustomFields([]);
      
      // Only refetch if everything succeeds
      setTimeout(() => {
        refetch();
      }, 1000);
    } catch (error) {
      // If validation fails, don't close the modal
      // The form will show validation errors automatically
      console.log("Validation or API error:", error);
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    caseForm.resetFields();
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    
    const customFieldValues = record.customfield_subcase 
      ? record.customfield_subcase.map(item => {
          const field = customFields.find(cf => cf.id === item.value);
          return field ? field.title : item.customfield_name;
        })
      : [];
    
    editForm.setFieldsValue({
      number: record.number,
      case_name: record.casename,
      remark: record.remark,
      customfield_subcase: customFieldValues,
    });
    setIsEditModalOpen(true);
  };

  const handleEditModalOk = async () => {
    try {
      const values = await editForm.validateFields();
      // Convert editCustomFields to API format
      const formattedCustomFields = editCustomFields.map((f) => {
        const field = customFields.find(cf => cf.id === f.value);
        return {
          type: field?.type || 1,
          value: field?.id || 0,
          customfield_name: field?.title || '',
          number: f.number,
        };
      });
      const updatedCase = {
        ...values,
        id: selectedRecord.id,
        customfield_subcase: formattedCustomFields,
      };
      updateSubCase(updatedCase);
      message.success("Case updated successfully");
      setIsEditModalOpen(false);
      editForm.resetFields();
      
      // Only refetch if everything succeeds
      setTimeout(() => {
        refetch();
      }, 1000);
    } catch (error) {
      // If validation fails, don't close the modal
      // The form will show validation errors automatically
      console.log("Validation or API error:", error);
    }
  };

  const handleEditModalCancel = () => {
    setIsEditModalOpen(false);
    editForm.resetFields();
    setSelectedRecord(null);
  };

  // เปิด/ปิด case3 (Subcase) — ปิด = ไม่โชว์ในหน้าทำงาน แต่หน้ารายงานยังเห็น
  const handleToggleEnable = async (record, checked) => {
    setTogglingId(record.id);
    try {
      await toggleEnable({ id: record.id, target: "case3", enable: checked });
      message.success(checked ? "เปิดแสดงแล้ว" : "ปิดแล้ว (ซ่อนจากหน้าทำงาน)");
      refetch();
    } catch (e) {
      message.error("เปลี่ยนสถานะไม่สำเร็จ");
    } finally {
      setTogglingId(null);
    }
  };

  const columns = [
    {
      title: "แสดง",
      align: "center",
      width: 70,
      key: "enable",
      render: (record) => (
        <Switch
          size="small"
          checked={Number(record.enable) !== 0}
          loading={togglingId === record.id}
          onChange={(checked) => handleToggleEnable(record, checked)}
        />
      ),
    },
    {
      title: "ลำดับ",
      align: "center",
      width: 60,
      dataIndex: "number",
      key: "number",
    },
    {
      title: "Case Name",
      align: "center",
      dataIndex: "casename",
      key: "casename",
    },
    {
      title: "Remark",
      align: "center",
      dataIndex: "remark",
      key: "remark",
    },
    {
      title: "Custom Fields",
      align: "center",
      dataIndex: "customfield_subcase",
      key: "customfield_subcase",
      render: (customfields) => {
        if (!customfields || customfields.length === 0) {
          return <span style={{ color: '#bbb' }}>ไม่มี</span>;
        }
        return (
          <div>
            {customfields
              .sort((a, b) => a.number - b.number)
              .map((field, idx) => (
                <div key={idx} style={{ fontSize: '12px', marginBottom: '2px' }}>
                  {field.number}. {field.customfield_name}
                </div>
              ))}
          </div>
        );
      },
    },
    {
      title: "Create Date",
      align: "center",
      dataIndex: "create_date",
      key: "create_date",
      render: (text) => {
        return (
          <span>
            {text
              ? `${new Date(text).toLocaleDateString()} ${new Date(
                  text
                ).toLocaleTimeString()}`
              : ""}
          </span>
        );
      },
    },
    {
      title: "การจัดการ",
      align: "center",
      key: "actions",
      render: (record) => (
        <Button type="primary" onClick={() => handleEdit(record)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button type="primary" onClick={() => setIsModalOpen(true)}>
          Add Case
        </Button>
      </div>

      <Table dataSource={data} columns={columns} pagination={false} bordered />

      <Modal
        title="Add New Case"
        open={isModalOpen}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
      >
        <Form form={caseForm} layout="vertical">
          <Form.Item
            name="number"
            label="ลำดับ"
            rules={[{ required: true, message: "Please input case number!" }]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item
            name="case_name"
            label="Case Name"
            rules={[{ required: true, message: "Please input case name!" }]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item
            name="remark"
            label="หมายเหตุ"
            rules={[{ required: true, message: "Please input remark!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Custom Fields">
            <Table
              dataSource={editCustomFields}
              rowKey="key"
              pagination={false}
              bordered
              size="small"
              columns={[
                {
                  title: 'ลำดับ',
                  dataIndex: 'number',
                  align: 'center',
                  width: 60,
                },
                {
                  title: 'Custom Field',
                  dataIndex: 'value',
                  align: 'center',
                  render: (value, record) => {
                    if (!customFields || customFields.length === 0) {
                      return <span style={{ color: '#bbb' }}>Loading...</span>;
                    }
                    const field = customFields.find(f => f.id === value);
                    return (
                      <Select
                        value={value ?? undefined}
                        style={{ width: 180 }}
                        onChange={val => handleChangeCustomField(record.key, val)}
                        showSearch
                        optionFilterProp="children"
                        placeholder="เลือก Custom Field"
                        
                      >
                        {customFields
                          .filter(f =>
                            f.id === value ||
                            !editCustomFields.some(cf => cf.value === f.id)
                          )
                          .map(f => (
                            <Select.Option key={f.id} value={f.id}>{f.title}</Select.Option>
                          ))}
                      </Select>
                    );
                  },
                },
                {
                  title: '',
                  dataIndex: 'action',
                  align: 'center',
                  width: 40,
                  render: (_, record) => (
                    <Button
                      icon={<MinusCircleOutlined />}
                      danger
                      size="small"
                      onClick={() => handleRemoveCustomField(record.key)}
                    />
                  ),
                },
              ]}
            />
            <Button
              type="dashed"
              onClick={handleAddCustomField}
              style={{ width: '100%', marginTop: 8 }}
              icon={<PlusOutlined />}
            >
              เพิ่ม Custom Field
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit Case"
        open={isEditModalOpen}
        onOk={handleEditModalOk}
        onCancel={handleEditModalCancel}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="number"
            label="ลำดับ"
            rules={[{ required: true, message: "Please input case number!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="case_name"
            label="Case Name"
            rules={[{ required: true, message: "Please input case name!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="remark"
            label="หมายเหตุ"
            rules={[{ required: true, message: "Please input remark!" }]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item label="Custom Fields">
            <Table
              dataSource={editCustomFields}
              rowKey="key"
              pagination={false}
              bordered
              size="small"
              columns={[
                {
                  title: 'ลำดับ',
                  dataIndex: 'number',
                  align: 'center',
                  width: 60,
                },
                {
                  title: 'Custom Field',
                  dataIndex: 'value',
                  align: 'center',
                  render: (value, record) => {
                    if (!customFields || customFields.length === 0) {
                      return <span style={{ color: '#bbb' }}>Loading...</span>;
                    }
                    const field = customFields.find(f => f.id === value);
                    return (
                      <Select
                        value={value ?? undefined}
                        style={{ width: 180 }}
                        onChange={val => handleChangeCustomField(record.key, val)}
                        showSearch
                        optionFilterProp="children"
                        placeholder="เลือก Custom Field"
                        
                      >
                        {customFields
                          .filter(f =>
                            f.id === value ||
                            !editCustomFields.some(cf => cf.value === f.id)
                          )
                          .map(f => (
                            <Select.Option key={f.id} value={f.id}>{f.title}</Select.Option>
                          ))}
                      </Select>
                    );
                  },
                },
                {
                  title: '',
                  dataIndex: 'action',
                  align: 'center',
                  width: 40,
                  render: (_, record) => (
                    <Button
                      icon={<MinusCircleOutlined />}
                      danger
                      size="small"
                      onClick={() => handleRemoveCustomField(record.key)}
                    />
                  ),
                },
              ]}
            />
            <Button
              type="dashed"
              onClick={handleAddCustomField}
              style={{ width: '100%', marginTop: 8 }}
              icon={<PlusOutlined />}
            >
              เพิ่ม Custom Field
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default CaseFormTable;
